import React, { createContext, useCallback, useContext, useEffect, useState } from "react";
import { Alert, Button } from "react-bootstrap";
import { useNavigate } from "react-router-dom";

// ── PlanGateProvider (TASK-18) ────────────────────────────────────────────────
// Listens to the "plan-gate-error" CustomEvent dispatched by the 402 interceptor
// in src/api/client.js and renders the UpgradeNudge as a non-blocking banner.
//
// To activate globally: add <PlanGateProvider> to the provider tree in src/App.js
// (TASK-19 wires this up once the provider tree is confirmed stable).
//
// Usage in App.js (TASK-19):
//   import { PlanGateProvider } from './providers/PlanGateProvider';
//   <PlanGateProvider><AppRouter /></PlanGateProvider>

const PlanGateContext = createContext(null);

const FALLBACK_MSG =
  "Con 4 bloques de estudiantes pagarías $100.000 extra; el plan Academia te da " +
  "200 estudiantes + honorarios + multi-sede por $100.000 más. ¿Mejoramos tu plan?";

function PlanGateProvider({ children }) {
  const [nudge, setNudge] = useState(null); // { suggest_upgrade, upgrade_to, message }
  const navigate = useNavigate();

  useEffect(() => {
    function onPlanGateError(event) {
      const detail = event.detail ?? {};
      if (detail.suggest_upgrade) setNudge(detail);
    }
    window.addEventListener("plan-gate-error", onPlanGateError);
    return () => window.removeEventListener("plan-gate-error", onPlanGateError);
  }, []);

  const dismiss = useCallback(() => setNudge(null), []);

  return (
    <PlanGateContext.Provider value={{ nudge, dismiss }}>
      {nudge && (
        <div
          style={{ position: "fixed", bottom: "1rem", right: "1rem", zIndex: 1055, maxWidth: "420px" }}
          role="status"
          aria-live="polite"
        >
          <Alert
            variant="warning"
            dismissible
            onClose={dismiss}
            className="shadow mb-0"
          >
            <Alert.Heading className="h6 mb-1">Sugerencia de plan</Alert.Heading>
            <p className="mb-2 small">{nudge.message || FALLBACK_MSG}</p>
            <Button
              size="sm"
              variant="warning"
              className="fw-semibold"
              onClick={() => {
                navigate("/plan");
                dismiss();
              }}
              aria-label="Ir a la pantalla de plan para mejorarlo"
            >
              Mejorar plan
            </Button>
          </Alert>
        </div>
      )}
      {children}
    </PlanGateContext.Provider>
  );
}

function usePlanGate() {
  return useContext(PlanGateContext);
}

export { PlanGateProvider, usePlanGate };
